import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Observable, combineLatest, map } from 'rxjs';
import { HrService } from '../../../services/human-resources/hr.service';
import { DocumentSummary, DocumentStatus, Employee, PersonalEventType } from '../../../models/hr-models';
import { switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { 
  faUserPlus, 
  faUserTie, 
  faFileAlt, 
  faCalendarPlus, 
  faChartPie, 
  faChartBar, 
  faInfoCircle, 
  faExclamationTriangle, 
  faBirthdayCake, 
  faAward, 
  faArrowRight, 
  faCalendarAlt,
  faSync 
} from '@fortawesome/free-solid-svg-icons';
import { environment } from '@environments/environment';

@Component({
  selector: 'app-hr-dashboard',
  standalone: true,
  imports: [CommonModule, RouterModule, FontAwesomeModule],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss']
})
export class HrDashboardComponent implements OnInit {
  // FontAwesome icons
  faUserPlus = faUserPlus;
  faUserTie = faUserTie;
  faFileAlt = faFileAlt;
  faCalendarPlus = faCalendarPlus;
  faChartPie = faChartPie;
  faChartBar = faChartBar;
  faInfoCircle = faInfoCircle;
  faExclamationTriangle = faExclamationTriangle;
  faBirthdayCake = faBirthdayCake;
  faAward = faAward;
  faArrowRight = faArrowRight;
  faCalendarAlt = faCalendarAlt;
  faSync = faSync;

  documentSummary$: Observable<DocumentSummary>;
  employees$: Observable<Employee[]>;
  upcomingBirthdays$: Observable<any[]>;
  upcomingAnniversaries$: Observable<any[]>;
  expiringDocuments$: Observable<any[]>;
  hasQuickStats$: Observable<boolean>;
  
  // User details
  userName: string | null = null;
  userRole: string | null = null;
  companyId: number | null = null;
  
  // Access control
  isAdmin: boolean = false;
  isHrManager: boolean = false;

  // Chart data
  documentChartData = {
    labels: ['Valid', 'Expiring', 'Expired', 'Missing'],
    datasets: [{
      data: [0, 0, 0, 0],
      backgroundColor: [
        '#2ec4b6', // Valid - Teal
        '#f9c74f', // Expiring - Yellow
        '#e63946', // Expired - Red
        '#6c757d'  // Missing - Gray
      ]
    }]
  };

  // Chart options
  chartOptions = {
    responsive: true,
    maintainAspectRatio: false
  };

  constructor(
    private hrService: HrService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.documentSummary$ = this.hrService.getDocumentSummary();
    this.employees$ = this.hrService.employees$;

    // Get upcoming birthdays for the next 30 days
    this.upcomingBirthdays$ = this.hrService.getPersonalEvents().pipe(
      switchMap(events => {
        // Filter to get only birthday events in the next 30 days
        const today = new Date();
        const thirtyDaysLater = new Date();
        thirtyDaysLater.setDate(today.getDate() + 30);
        
        const filteredEvents = events
          .filter(event => event.eventType === PersonalEventType.Birthday)
          .filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= today && eventDate <= thirtyDaysLater;
          })
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime();
          })
          .slice(0, 5); // Show only top 5
          
        // Return employees observable to join with events
        return this.employees$.pipe(
          map(employees => {
            // Join events with employee data
            return filteredEvents.map(event => {
              const employee = employees.find(e => e.id === event.employeeId);
              return {
                ...event,
                employee: employee || { name: 'Unknown' }
              };
            });
          })
        );
      })
    );

    // Get upcoming anniversaries for the next 30 days
    this.upcomingAnniversaries$ = this.hrService.getPersonalEvents().pipe(
      switchMap(events => {
        // Filter to get only anniversary events in the next 30 days
        const today = new Date();
        const thirtyDaysLater = new Date();
        thirtyDaysLater.setDate(today.getDate() + 30);
        
        const filteredEvents = events
          .filter(event => event.eventType === PersonalEventType.WorkAnniversary)
          .filter(event => {
            const eventDate = new Date(event.date);
            return eventDate >= today && eventDate <= thirtyDaysLater;
          })
          .sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateA.getTime() - dateB.getTime();
          })
          .slice(0, 5); // Show only top 5
          
        // Return employees observable to join with events
        return this.employees$.pipe(
          map(employees => {
            // Join events with employee data
            return filteredEvents.map(event => {
              const employee = employees.find(e => e.id === event.employeeId);
              return {
                ...event,
                employee: employee || { name: 'Unknown' }
              };
            });
          })
        );
      })
    );

    // Get expiring documents in the next 30 days
    this.expiringDocuments$ = this.hrService.getExpiringDocuments(30).pipe(
      map(documents => {
        return documents.sort((a, b) => {
          const dateA = new Date(a.expiryDate);
          const dateB = new Date(b.expiryDate);
          return dateA.getTime() - dateB.getTime();
        }).slice(0, 5); // Show only top 5
      })
    );

    // Check if any quick stats are available
    this.hasQuickStats$ = combineLatest([
      this.documentSummary$,
      this.upcomingBirthdays$,
      this.upcomingAnniversaries$
    ]).pipe(
      map(([summary, birthdays, anniversaries]) => {
        return (summary && summary.expiringDocuments > 0) || 
               (birthdays && birthdays.length > 0) || 
               (anniversaries && anniversaries.length > 0);
      })
    );
  }

  ngOnInit(): void {
   
      if (localStorage.getItem('auth_token')) {
                
        // Store user info if available
        if (localStorage.getItem('username')) {
          this.userName = localStorage.getItem('username');
        }
        
        if (localStorage.getItem('userRole')) {
          this.userRole = localStorage.getItem('userRole');
          this.isAdmin = localStorage.getItem('userRole') === 'admin';
          this.isHrManager = localStorage.getItem('userRole') === 'hr';
        }
        
        if (localStorage.getItem('companyId')) {
          this.companyId = parseInt(localStorage.getItem('companyId') || '0', 10);
        }
        
     
      } else {
        // If no token in URL, try to get from localStorage
        
        const token = localStorage.getItem('auth_token');
        this.userName = localStorage.getItem('username');
        this.userRole = localStorage.getItem('userRole');
        const companyIdStr = localStorage.getItem('companyId');
        this.companyId = companyIdStr ? parseInt(companyIdStr, 10) : null;
        
        this.isAdmin = this.userRole === 'admin';
        this.isHrManager = this.userRole === 'hr';
        
      }
      
      // If there's no valid token or user role, redirect to login
      if (!localStorage.getItem('auth_token') || !this.userRole) {
        alert('Your session has expired or you are not logged in. Please log in again.');
        this.router.navigate(['/hr/login']);
        return;
      }
      
      console.log('Dashboard: Authentication validation passed');
    
    
    console.log('Dashboard: Refreshing data...');
    this.hrService.refreshData();

    // Update chart data when summary changes
    this.documentSummary$.subscribe(summary => {
      this.updateChartData(summary);
    });
  }

  /**
   * Update the chart data with document summary
   */
  updateChartData(summary: DocumentSummary): void {
    this.documentChartData.datasets[0].data = [
      summary.validDocuments,
      summary.expiringDocuments,
      summary.expiredDocuments,
      summary.missingDocuments
    ];
  }

  /**
   * Format date for display
   */
  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString();
  }

  /**
   * Get days until a date
   */
  getDaysUntil(date: Date): number {
    const today = new Date();
    const targetDate = new Date(date);
    const diffTime = targetDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  /**
   * Get display text for days remaining
   */
  getDaysUntilText(date: Date): string {
    const daysUntil = this.getDaysUntil(date);
    if (daysUntil === 0) {
      return 'Today';
    } else if (daysUntil === 1) {
      return 'Tomorrow';
    } else {
      return `${daysUntil} days`;
    }
  }

  /**
   * Get class for displaying days remaining
   */
  getDaysUntilClass(date: Date): string {
    const daysUntil = this.getDaysUntil(date);
    if (daysUntil <= 3) {
      return 'badge bg-danger';
    } else if (daysUntil <= 7) {
      return 'badge bg-warning';
    } else {
      return 'badge bg-primary';
    }
  }

  /**
   * Get employee name by ID
   */
  getEmployeeName(employeeId: number): Observable<string> {
    return this.employees$.pipe(
      map(employees => {
        const employee = employees.find(e => e.id === employeeId);
        return employee ? employee.name : 'Unknown';
      })
    );
  }

  /**
   * Get the segment size for a chart segment
   */
  getSegmentSize(segmentType: string): string {
    if (!this.documentChartData.datasets[0].data.some(val => val > 0)) {
      // Default equal distribution if no data
      return '25%';
    }

    const total = this.documentChartData.datasets[0].data.reduce((sum, val) => sum + val, 0);

    let percentage = 0;
    switch(segmentType) {
      case 'valid':
        percentage = (this.documentChartData.datasets[0].data[0] / total) * 100;
        break;
      case 'expiring':
        percentage = (this.documentChartData.datasets[0].data[1] / total) * 100;
        break;
      case 'expired':
        percentage = (this.documentChartData.datasets[0].data[2] / total) * 100;
        break;
      case 'missing':
        percentage = (this.documentChartData.datasets[0].data[3] / total) * 100;
        break;
    }

    // Format percentage as a CSS-compatible value
    return `${percentage}%`;
  }

  /**
   * Add an HR manager (admin only)
   */
  addHrManager() {
    if (!this.isAdmin && !this.isSuperUser) {
      alert('Only admin and superuser users can add HR managers');
      return;
    }
    
    // Navigate to the add HR manager form
    this.router.navigate(['/hr/add-hr-manager']);
  }

  /**
   * Check if current user is a superuser
   */
  get isSuperUser(): boolean {
    return this.userRole === 'superuser';
  }

  /**
   * Check if user has permission to add HR managers (admin or superuser)
   */
  get canAddHrManager(): boolean {
    return this.isAdmin || this.isSuperUser;
  }
}
